import { createServerFn } from "@tanstack/react-start";

export type LocationGalleryItem = { path: string; caption?: string };

export type PublicLocationPage = {
  title: string;
  intro: string;
  body: string;
  mapUrl: string | null;
  gallery: { url: string; caption?: string }[];
};

async function signPath(path: string | null | undefined): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin.storage
    .from("location")
    .createSignedUrl(path, 60 * 60 * 24 * 7);
  return data?.signedUrl ?? null;
}

export const getLocationPage = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicLocationPage> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("location", 300_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("location_page")
      .select("title, intro, body, map_path, gallery")
      .eq("id", "default")
      .maybeSingle();

    if (!data) {
      return {
        title: "Stadspark Berg en Bos",
        intro: "",
        body: "",
        mapUrl: null,
        gallery: [],
      };
    }

    const gallery = (data.gallery as LocationGalleryItem[] | null) ?? [];
    const mapUrl = await signPath(data.map_path as string | null);
    const signedGallery = await Promise.all(
      gallery.map(async (item) => ({
        url: (await signPath(item.path)) ?? "",
        caption: item.caption,
      })),
    );

    return {
      title: (data.title as string) ?? "Stadspark Berg en Bos",
      intro: (data.intro as string) ?? "",
      body: (data.body as string) ?? "",
      mapUrl,
      gallery: signedGallery.filter((g) => g.url),
    };
    });
  },
);